import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { DeviceDetectorService } from 'ngx-device-detector';
import posthog from 'posthog-js';
import { take } from 'rxjs';
import { ContentModalComponent } from '../../basic/modal/content-modal/content-modal.component';
import { TextImageModalComponent } from '../../basic/modal/text-image-modal/text-image-modal.component';
import { ToLocaleFormat } from '../../basic/pipes/to-locale-format.pipe';
import { GoalService } from '../../core/goal/goal.service';
import { Interview, InterviewType, SESSION_LENGTH, canFreeTrial } from '../../core/interview/interview';
import { InterviewService } from '../../core/interview/interview.service';
import { PostHogEvent } from '../../core/posthog/posthog.event';
import { ResumeService } from '../../core/resume/resume.service';
import { NewInterviewCopilotComponent } from '../../domain/interview-copilot/new-interview-copilot/new-interview-copilot.component';
import { InterviewSidebarComponent } from '../home/interview-sidebar/interview-sidebar.component';
import { InterviewStatus } from './../../core/interview/interview';
import { UserClerkService } from './../../core/user/user-clerk.service';

@Component({
  selector: 'app-interview-copilot',
  standalone: true,
  templateUrl: './interview-copilot.component.html',
  styleUrl: './interview-copilot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,

    NzIconModule,
    NzButtonModule,
    NzModalModule,
    NzInputModule,
    NzAutocompleteModule,
    NzSegmentedModule,
    NzTableModule,
    NzSelectModule,
    NzAlertModule,
    NzTabsModule,
    NzTagModule,
    NzPopconfirmModule,

    MatTooltip,

    InterviewSidebarComponent,
    NewInterviewCopilotComponent,
    ToLocaleFormat,
    TextImageModalComponent,
    ContentModalComponent,
  ],
})
export class InterviewCopilotComponent {
  InterviewType = InterviewType;

  InterviewStatus = InterviewStatus;

  isNewInterviewVisible = false;

  isNotEnoughPointsVisible = false;
  isFreeTrialVisible = false;

  selectedTabIndex = 0;

  showMobileModal = signal<boolean>(false);

  goalList = toSignal(this.goalService.goalList$(), { initialValue: [] });
  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  interviewList = toSignal(this.interviewService.interviewList$(), {
    initialValue: [],
  });

  selectedInterview?: Interview;

  user = toSignal(this.userClerkService.getUser$());

  getLeftMinutes(interview: Interview) {
    return SESSION_LENGTH + (interview.statusTime?.diffNow('minutes').minutes ?? 0);
  }

  getInterviewType(interviewType: InterviewType) {
    if (interviewType === InterviewType.code) {
      return 'Coding Interview';
    } else {
      return 'Normal Interview';
    }
  }

  constructor(
    private router: Router,
    private goalService: GoalService,
    private modal: NzModalService,
    private resumeService: ResumeService,
    private interviewService: InterviewService,
    private deviceService: DeviceDetectorService,
    private userClerkService: UserClerkService
  ) {
    effect(
      () => {
        const showNotEnoughPointsModal = this.interviewService.showNotEnoughPointsModal();

        if (showNotEnoughPointsModal) {
          console.log('showNotEnoughPointsModal');
          this.isNotEnoughPointsVisible = true;
        }
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngOnInit() {}

  onNoGoalsErrorClick() {
    this.router.navigate(['interview-goals']);
  }

  onNoResumeErrorClick() {
    this.router.navigate(['interview-resume']);
  }

  getGoal(id: string) {
    return this.goalList().find((g) => g.id === id);
  }

  getResume(id: string) {
    return this.resumeList().find((r) => r.id === id);
  }

  onNewInterviewClick() {
    this.isNewInterviewVisible = true;
  }

  onDeleteInterviewClick(data: Interview) {
    this.interviewService.deleteInterview$(data).pipe(take(1)).subscribe();
  }

  hasOngoingInterview() {
    return this.interviewList().some((i) => this.isOngoing(i));
  }

  isOngoing(interview: Interview) {
    return this.interviewService.isOngoing(interview);
  }

  onContinueInterviewClick(interview: Interview) {
    if (interview.type === InterviewType.code) {
      posthog.capture(PostHogEvent.interviewCodingContinue, { interviewId: interview.id });
    } else {
      posthog.capture(PostHogEvent.interviewNormalContinue, { interviewId: interview.id });
    }
    this.router.navigate(['', 'interview-assistant', interview.id]);
  }

  onLaunchInterviewClick(interview: Interview) {
    if (this.deviceService.isMobile()) {
      this.showMobileModal.set(true);
    } else if (!this.interviewService.isCreditsEnough(interview)) {
      if (canFreeTrial(this.user()!.publicMetadata)) {
        this.selectedInterview = interview;
        this.isFreeTrialVisible = true;
      } else {
        this.isNotEnoughPointsVisible = true;
      }
    } else {
      this.interviewService
        .launchInterview$(interview)
        .pipe(take(1))
        .subscribe(() => {
          this.router.navigate(['', 'interview-assistant', interview.id]);
        });
    }
  }

  onGetMorePointsClick() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/pricing']));
    window.open(url, '_blank');
  }

  onFreeTrialClick() {
    if (this.deviceService.isMobile()) {
      this.showMobileModal.set(true);
    } else {
      const interview = this.selectedInterview!;

      this.interviewService
        .launchFreeTrialInterview$(interview)
        .pipe(take(1))
        .subscribe(() => {
          this.router.navigate(['', 'interview-assistant', interview.id]);
        });
    }
  }
}
